import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";

const CookiePolicyPage = () => {
  const fadeUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const handleOpenPreferences = () => {
    // Clear existing consent to show banner again
    localStorage.removeItem("cookieConsent");
    window.location.reload();
  };

  return (
    <div data-testid="cookie-policy-page">
      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-mist">
        <div className="container-custom">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-navy/70 hover:text-berry transition-colors mb-8"
          >
            <ArrowLeft size={16} />
            Terug naar home
          </Link>
          <motion.h1 {...fadeUp} className="heading-1 mb-4">
            Cookiebeleid
          </motion.h1>
          <motion.p {...fadeUp} transition={{ delay: 0.1 }} className="body-large max-w-2xl">
            Informatie over het gebruik van cookies op deze website.
          </motion.p>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto prose prose-lg">
            <motion.div {...fadeUp}>
              <h2 className="font-serif text-2xl text-navy mb-4">
                Wat zijn cookies?
              </h2>
              <p className="text-navy/70 leading-relaxed mb-6">
                Cookies zijn kleine tekstbestanden die op jouw computer, tablet of smartphone 
                worden geplaatst wanneer je onze website bezoekt. Cookies helpen ons om de 
                website goed te laten functioneren, de website veiliger te maken, een betere 
                gebruikerservaring te bieden en te begrijpen hoe de website presteert.
              </p>

              <h2 className="font-serif text-2xl text-navy mb-4 mt-10">
                Welke cookies gebruiken wij?
              </h2>
              <p className="text-navy/70 leading-relaxed mb-6">
                Wij maken onderscheid tussen drie soorten cookies:
              </p>

              {/* Functional Cookies */}
              <div className="bg-mist p-6 rounded-sm mb-6">
                <h3 className="font-semibold text-navy mb-3">
                  1. Functionele cookies (noodzakelijk)
                </h3>
                <p className="text-navy/70 text-sm mb-4">
                  Deze cookies zijn essentieel voor het functioneren van de website. 
                  Zonder deze cookies kunnen bepaalde onderdelen van de website niet werken.
                </p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 text-navy">Cookie</th>
                      <th className="text-left py-2 text-navy">Doel</th>
                      <th className="text-left py-2 text-navy">Bewaartermijn</th>
                    </tr>
                  </thead>
                  <tbody className="text-navy/70">
                    <tr className="border-b border-gray-100">
                      <td className="py-2">cookieConsent</td>
                      <td className="py-2">Onthoudt jouw cookievoorkeuren</td>
                      <td className="py-2">1 jaar</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-2">session</td>
                      <td className="py-2">Sessie-identificatie</td>
                      <td className="py-2">Sessie</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Analytics Cookies */}
              <div className="bg-mist p-6 rounded-sm mb-6">
                <h3 className="font-semibold text-navy mb-3">
                  2. Analytische cookies (optioneel)
                </h3>
                <p className="text-navy/70 text-sm mb-4">
                  Met deze cookies verzamelen wij informatie over hoe bezoekers de website 
                  gebruiken. Dit helpt ons om de website te verbeteren. Deze cookies worden 
                  alleen geplaatst met jouw toestemming.
                </p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 text-navy">Cookie</th>
                      <th className="text-left py-2 text-navy">Doel</th>
                      <th className="text-left py-2 text-navy">Bewaartermijn</th>
                    </tr>
                  </thead>
                  <tbody className="text-navy/70">
                    <tr className="border-b border-gray-100">
                      <td className="py-2">_ga</td>
                      <td className="py-2">Google Analytics - unieke bezoeker identificatie</td>
                      <td className="py-2">2 jaar</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-2">_gid</td>
                      <td className="py-2">Google Analytics - sessie identificatie</td>
                      <td className="py-2">24 uur</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-2">_gat</td>
                      <td className="py-2">Google Analytics - rate limiting</td>
                      <td className="py-2">1 minuut</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Tracking/Marketing Cookies */}
              <div className="bg-mist p-6 rounded-sm mb-6">
                <h3 className="font-semibold text-navy mb-3">
                  3. Tracking cookies (optioneel)
                </h3>
                <p className="text-navy/70 text-sm mb-4">
                  Tracking cookies worden gebruikt voor marketingdoeleinden. Hiermee kunnen 
                  wij relevante advertenties tonen en de effectiviteit van campagnes meten. 
                  Deze cookies worden alleen geplaatst met jouw expliciete toestemming.
                </p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 text-navy">Cookie</th>
                      <th className="text-left py-2 text-navy">Doel</th>
                      <th className="text-left py-2 text-navy">Bewaartermijn</th>
                    </tr>
                  </thead>
                  <tbody className="text-navy/70">
                    <tr className="border-b border-gray-100">
                      <td className="py-2">_fbp</td>
                      <td className="py-2">Facebook Pixel - advertentie tracking</td>
                      <td className="py-2">3 maanden</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-2">li_sugr</td>
                      <td className="py-2">LinkedIn Insight Tag</td>
                      <td className="py-2">3 maanden</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="font-serif text-2xl text-navy mb-4 mt-10">
                Jouw cookievoorkeuren beheren
              </h2>
              <p className="text-navy/70 leading-relaxed mb-4">
                Je kunt op elk moment jouw cookievoorkeuren wijzigen. Klik op onderstaande knop 
                om jouw voorkeuren opnieuw in te stellen:
              </p>
              <button
                onClick={handleOpenPreferences}
                data-testid="change-cookie-preferences"
                className="bg-burgundy text-white px-6 py-3 text-sm font-semibold hover:bg-navy transition-colors mb-6"
              >
                Cookie voorkeuren wijzigen
              </button>

              <h2 className="font-serif text-2xl text-navy mb-4 mt-10">
                Cookies blokkeren of verwijderen
              </h2>
              <p className="text-navy/70 leading-relaxed mb-4">
                Je kunt ook via jouw browserinstellingen cookies blokkeren of verwijderen. 
                Houd er rekening mee dat het blokkeren van bepaalde cookies de werking 
                van de website kan beïnvloeden.
              </p>
              <ul className="text-navy/70 mb-6 space-y-2 list-disc pl-6">
                <li>
                  <a 
                    href="https://support.google.com/chrome/answer/95647" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-berry hover:underline"
                  >
                    Google Chrome
                  </a>
                </li>
                <li>
                  <a 
                    href="https://support.mozilla.org/nl/kb/cookies-verwijderen-gegevens-wissen-websites-opgeslagen" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-berry hover:underline"
                  >
                    Mozilla Firefox
                  </a>
                </li>
                <li>
                  <a 
                    href="https://support.apple.com/nl-nl/guide/safari/sfri11471/mac" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-berry hover:underline"
                  >
                    Safari
                  </a>
                </li>
                <li>
                  <a 
                    href="https://support.microsoft.com/nl-nl/microsoft-edge/cookies-verwijderen-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-berry hover:underline"
                  >
                    Microsoft Edge
                  </a>
                </li>
              </ul>

              <h2 className="font-serif text-2xl text-navy mb-4 mt-10">
                Wijzigingen
              </h2>
              <p className="text-navy/70 leading-relaxed mb-6">
                Wij kunnen dit cookiebeleid van tijd tot tijd aanpassen. Wijzigingen worden 
                op deze pagina gepubliceerd. Wij raden je aan dit beleid regelmatig te 
                raadplegen.
              </p>

              <div className="mt-12 p-6 bg-mist rounded-sm">
                <h3 className="font-semibold text-navy mb-2">Vragen?</h3>
                <p className="text-navy/70 text-sm">
                  Heb je vragen over ons cookiebeleid? Neem dan contact met ons op via{" "}
                  <a href="mailto:rabia@regieopcashflow.nl" className="text-berry hover:underline">
                    rabia@regieopcashflow.nl
                  </a>
                </p>
              </div>

              <p className="text-navy/70 text-sm mt-8">
                Laatst bijgewerkt: {new Date().toLocaleDateString('nl-NL', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CookiePolicyPage;
